import type {
  OperatorProfileLibrary,
  OperatorProfilePolicyDocument,
} from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { asFormError } from "../format.js";
import { SbaiFreezeNotice, useSbaiFreeze } from "./SbaiFreeze.js";

type Profile = OperatorProfileLibrary["profiles"][number];
type Game = Profile["game"];
const buyInPaths = ["minBuyInMinor", "maxBuyInMinor", "defaultBuyInMinor"];
const labels: Record<string, string> = {
  smallBlindMinor: "Small blind (minor units)",
  bigBlindMinor: "Big blind (minor units)",
  buyInStepMinor: "Buy-in step (minor units)",
  rakeCapMinor: "Rake cap (minor units)",
  rakeBasisPoints: "Rake (%)",
  turnDurationMs: "Turn duration (ms)",
  handTimeBankMs: "Time bank (ms)",
  playerTurnTimeoutMs: "Player turn (ms)",
  insuranceDecisionTimeoutMs: "Insurance decision (ms)",
  settlementPresentationMs: "Settlement display (ms)",
  aggregateInsideMaxMinor: "Inside bet total maximum (minor units)",
  aggregateOutsideMaxMinor: "Outside bet total maximum (minor units)",
  minimumPositionBetMinor: "Minimum position bet (minor units)",
  chipDenominationsMinor: "Chip values (minor units, comma separated)",
  bettingDurationMs: "Betting duration (ms)",
  resultDisplayDurationMs: "Result display (ms)",
};
function label(path: string) {
  return (
    labels[path] ??
    path
      .replace("sideWagers.perfectPairs.", "Perfect Pairs ")
      .replace("sideWagers.twentyOnePlusThree.", "21+3 ")
      .replace("mainWager.", "Main bet ")
      .replace("chipDenominations", "chips (minor units, comma separated)")
  );
}
function get(config: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((v, k) => (v as Record<string, unknown>)[k], config);
}
function set(config: Record<string, unknown>, path: string, value: unknown) {
  const parts = path.split(".");
  let target = config;
  for (const key of parts.slice(0, -1)) target = target[key] as Record<string, unknown>;
  target[parts.at(-1)!] = value;
}
function bb(amount: unknown, blind: unknown) {
  const scaled = (BigInt(String(amount)) * 1_000_000n) / BigInt(String(blind));
  return `${scaled / 1_000_000n}.${String(scaled % 1_000_000n).padStart(6, "0")}`.replace(
    /\.?0+$/,
    "",
  );
}
function rakeInput(value: string) {
  if (!/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(value))
    throw new Error("Rake must have at most two decimal places.");
  const [whole, fraction = ""] = value.split(".");
  return Number(BigInt(whole!) * 100n + BigInt(fraction.padEnd(2, "0")));
}

export function OperatorProfiles({
  game,
  role,
  onEnabled,
  onChanged,
}: {
  game: Game;
  role: string;
  onEnabled: (value: boolean) => void;
  onChanged: () => Promise<unknown>;
}) {
  const [library, setLibrary] = useState<OperatorProfileLibrary | null>(null);
  const [error, setError] = useState<string | null>(null),
    [notice, setNotice] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Profile | null>(null),
    [policy, setPolicy] = useState<OperatorProfilePolicyDocument | null>(null);
  const [name, setName] = useState(""),
    [fields, setFields] = useState<Record<string, string>>({}),
    [buyIn, setBuyIn] = useState({ minimum: "40", maximum: "100", default: "100" });
  const [reason, setReason] = useState(""),
    [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [policyDraft, setPolicyDraft] = useState<OperatorProfilePolicyDocument | null>(null),
    [signature, setSignature] = useState("");
  const freeze = useSbaiFreeze();
  const mayWrite = freeze.canWrite && (role === "admin" || role === "super_admin");
  const load = useCallback(async () => {
    try {
      const value = await api.operatorProfiles();
      setLibrary(value);
      onEnabled(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        onEnabled(false);
        return;
      }
      setError(asFormError(e));
    }
  }, [onEnabled]);
  useEffect(() => {
    void load();
  }, [load]);
  if (!library)
    return error ? (
      <div className="banner" role="alert">
        {error}
      </div>
    ) : null;
  const policies = library.policies.filter((p) => p.document.game === game);
  const profiles = library.profiles.filter((p) => p.game === game);
  function open(document: OperatorProfilePolicyDocument, source?: Profile, copy = false) {
    const config = source?.config ?? document.template;
    setPolicy(document);
    setSelected(copy ? null : (source ?? null));
    setName(source ? `${source.displayName}${copy ? " copy" : ""}` : "");
    setFields(
      Object.fromEntries(
        Object.keys(document.parameters)
          .filter((p) => !buyInPaths.includes(p))
          .map((p) => {
            const v = get(config, p) ?? get(document.template, p);
            return [
              p,
              p === "rakeBasisPoints"
                ? String(Number(v) / 100)
                : Array.isArray(v)
                  ? v.join(", ")
                  : String(v),
            ];
          }),
      ),
    );
    if (game === "poker")
      setBuyIn({
        minimum: bb(config.minBuyInMinor, config.bigBlindMinor),
        maximum: bb(config.maxBuyInMinor, config.bigBlindMinor),
        default: bb(config.defaultBuyInMinor, config.bigBlindMinor),
      });
    setReason("");
    setError(null);
    setNotice(null);
    setRequestId(crypto.randomUUID());
    setPolicyDraft(null);
  }
  async function perform(action: () => Promise<unknown>, message: string) {
    if (!mayWrite) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      setNotice(message);
      setPolicy(null);
      setPolicyDraft(null);
      setSelected(null);
      setRequestId(crypto.randomUUID());
      await load();
      await onChanged();
    } catch (e) {
      setError(asFormError(e));
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!policy) return;
    try {
      const config = structuredClone(policy.template);
      for (const [path, value] of Object.entries(fields)) {
        const original = get(policy.template, path);
        const rule = policy.parameters[path]!;
        set(
          config,
          path,
          rule.kind === "choices"
            ? value
                .split(",")
                .map((v) => v.trim())
                .map((v) => (typeof (original as unknown[])[0] === "number" ? Number(v) : v))
            : path === "rakeBasisPoints"
              ? rakeInput(value)
              : typeof original === "number"
                ? Number(value)
                : value,
        );
      }
      await perform(
        () =>
          api.saveOperatorProfile({
            requestId,
            ...(selected ? { profileId: selected.profileId } : {}),
            expectedRevision: selected?.revision ?? 0,
            policyId: policy.policyId,
            policyRevision: policy.revision,
            displayName: name,
            config,
            ...(game === "poker" ? { pokerBuyInBB: buyIn } : {}),
            reason,
          }),
        "Profile revision saved. Select it below to apply it to a table.",
      );
    } catch (e) {
      setError(asFormError(e));
    }
  }
  return (
    <section className="card operator-profiles">
      <SbaiFreezeNotice freeze={freeze} />
      <div className="card-bar">
        <strong>Operator profile library</strong>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void load()}>
          Refresh library
        </button>
      </div>
      <p>
        Saving creates an immutable revision. Applying it to a table is a separate action. Amounts
        use minor units (100 = 1 EUR or XTS).
      </p>
      {error ? (
        <div className="banner" role="alert">
          {error}
        </div>
      ) : null}
      {notice ? <p role="status">{notice}</p> : null}
      {mayWrite ? (
        <div className="approved-profile-grid">
          {policies.map((p) => (
            <div key={p.document.policyId}>
              <strong>
                {p.document.currency} · {p.document.rulesetId}
              </strong>
              <p>
                Policy revision {p.document.revision} ·{" "}
                {p.document.currency === "XTS"
                  ? "Test currency"
                  : p.realMoneyAccepted
                    ? "Signed scope accepted"
                    : "Real-money scope not accepted"}
              </p>
              <button type="button" className="btn btn-sm" onClick={() => open(p.document)}>
                Create profile
              </button>
              {role === "super_admin" ? (
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => {
                    setPolicy(null);
                    setPolicyDraft({
                      ...structuredClone(p.document),
                      revision: p.document.revision + 1,
                    });
                    setSignature("");
                    setReason("");
                    setRequestId(crypto.randomUUID());
                  }}
                >
                  Revise policy
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      <details>
        <summary>Profiles and revision history ({profiles.length})</summary>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Profile</th>
                <th>Revision</th>
                <th>Currency</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => {
                const document = policies.find((x) => x.document.policyId === p.policyId)?.document;
                const latest = !profiles.some(
                  (other) => other.profileId === p.profileId && other.revision > p.revision,
                );
                return (
                  <tr key={`${p.profileId}:${p.revision}`}>
                    <td>
                      {p.displayName}
                      <details>
                        <summary>Snapshot</summary>
                        <pre>{JSON.stringify(p.config, null, 2)}</pre>
                      </details>
                    </td>
                    <td>{p.revision}</td>
                    <td>{p.currency}</td>
                    <td>{p.archived ? "Archived" : latest ? "Latest" : "Historical"}</td>
                    <td>
                      {mayWrite && document && !p.archived ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-xs"
                            onClick={() => open(document, p, true)}
                          >
                            Copy
                          </button>
                          {latest ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-xs"
                                onClick={() => open(document, p)}
                              >
                                Edit revision
                              </button>
                              <button
                                type="button"
                                className="btn btn-quiet btn-xs"
                                onClick={() => {
                                  setSelected(p);
                                  setPolicy(null);
                                  setPolicyDraft(null);
                                  setReason("");
                                  setRequestId(crypto.randomUUID());
                                }}
                              >
                                Archive
                              </button>
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
      {policy ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <h3>{selected ? "New revision" : "New profile"}</h3>
          <label>
            Profile name
            <input required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <div className="approved-profile-grid">
            {Object.entries(fields).map(([path, value]) => {
              const rule = policy.parameters[path]!;
              return (
                <label key={path}>
                  {label(path)}
                  <input
                    required
                    value={value}
                    onChange={(e) => setFields({ ...fields, [path]: e.target.value })}
                  />
                  <small>
                    {rule.kind === "integer"
                      ? `Allowed: ${path === "rakeBasisPoints" ? Number(rule.min) / 100 : rule.min} – ${path === "rakeBasisPoints" ? Number(rule.max) / 100 : rule.max}`
                      : `Allowed chips: ${rule.values.join(", ")}`}
                  </small>
                </label>
              );
            })}
            {game === "poker"
              ? (["minimum", "maximum", "default"] as const).map((key) => (
                  <label key={key}>
                    {key} buy-in (BB)
                    <input
                      required
                      value={buyIn[key]}
                      onChange={(e) => setBuyIn({ ...buyIn, [key]: e.target.value })}
                    />
                    <small>
                      Allowed: {policy.pokerBuyInBB?.[key].min} – {policy.pokerBuyInBB?.[key].max}{" "}
                      BB
                    </small>
                  </label>
                ))
              : null}
          </div>
          <label>
            Reason
            <textarea
              required
              minLength={5}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button className="btn" disabled={busy || !freeze.canWrite} type="submit">
            Save revision
          </button>
          <button
            type="button"
            className="btn btn-quiet"
            onClick={() => {
              setPolicy(null);
              setSelected(null);
            }}
          >
            Cancel
          </button>
        </form>
      ) : null}
      {selected && !policy && !policyDraft ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void perform(
              () =>
                api.archiveOperatorProfile({
                  requestId,
                  profileId: selected.profileId,
                  expectedRevision: selected.revision,
                  reason,
                }),
              "Profile archived. Historical snapshots are retained.",
            );
          }}
        >
          <h3>Archive {selected.displayName}</h3>
          <p>Assigned or pending profiles cannot be archived.</p>
          <label>
            Reason
            <input
              required
              minLength={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button className="btn" type="submit" disabled={busy || !freeze.canWrite}>
            Archive profile
          </button>
          <button className="btn btn-quiet" type="button" onClick={() => setSelected(null)}>
            Cancel
          </button>
        </form>
      ) : null}
      {policyDraft && mayWrite && role === "super_admin" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void perform(
              () =>
                api.saveOperatorPolicy({
                  requestId,
                  expectedRevision: policyDraft.revision - 1,
                  document: policyDraft,
                  ...(signature ? { signatureBase64: signature } : {}),
                  reason,
                }),
              "Policy revision saved.",
            );
          }}
        >
          <h3>Policy revision {policyDraft.revision}</h3>
          <p>
            These bounds control what operators may save. EUR changes require an external signature
            over this exact policy document.
          </p>
          <div className="approved-profile-grid">
            {Object.entries(policyDraft.parameters).map(([path, rule]) => (
              <fieldset key={path}>
                <legend>
                  {path === "rakeBasisPoints" ? "Rake (basis points; 100 = 1%)" : label(path)}
                </legend>
                {rule.kind === "integer" ? (
                  (["min", "max"] as const).map((key) => (
                    <label key={key}>
                      {key}
                      <input
                        value={rule[key]}
                        onChange={(e) =>
                          setPolicyDraft({
                            ...policyDraft,
                            parameters: {
                              ...policyDraft.parameters,
                              [path]: { ...rule, [key]: e.target.value },
                            },
                          })
                        }
                      />
                    </label>
                  ))
                ) : (
                  <label>
                    Allowed chips
                    <input
                      value={rule.values.join(", ")}
                      onChange={(e) =>
                        setPolicyDraft({
                          ...policyDraft,
                          parameters: {
                            ...policyDraft.parameters,
                            [path]: {
                              ...rule,
                              values: e.target.value.split(",").map((v) => v.trim()),
                            },
                          },
                        })
                      }
                    />
                  </label>
                )}
              </fieldset>
            ))}
            {policyDraft.pokerBuyInBB
              ? (["minimum", "maximum", "default"] as const).map((name) => (
                  <fieldset key={name}>
                    <legend>{name} buy-in BB</legend>
                    {(["min", "max"] as const).map((key) => (
                      <label key={key}>
                        {key}
                        <input
                          value={policyDraft.pokerBuyInBB![name][key]}
                          onChange={(e) =>
                            setPolicyDraft({
                              ...policyDraft,
                              pokerBuyInBB: {
                                ...policyDraft.pokerBuyInBB!,
                                [name]: {
                                  ...policyDraft.pokerBuyInBB![name],
                                  [key]: e.target.value,
                                },
                              },
                            })
                          }
                        />
                      </label>
                    ))}
                  </fieldset>
                ))
              : null}
          </div>
          <details>
            <summary>Exact policy document for external signing</summary>
            <pre>{JSON.stringify(policyDraft, null, 2)}</pre>
          </details>
          {policyDraft.currency === "EUR" ? (
            <label>
              External Ed25519 signature (base64)
              <textarea required value={signature} onChange={(e) => setSignature(e.target.value)} />
            </label>
          ) : null}
          <label>
            Reason
            <textarea
              required
              minLength={5}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button className="btn" disabled={busy || !freeze.canWrite} type="submit">
            Save policy revision
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => setPolicyDraft(null)}>
            Cancel
          </button>
        </form>
      ) : null}
    </section>
  );
}
